/**
 * Cálculo de un combo o receta (spec 20). Función pura: la usan la pantalla (vista previa y carrito) y el servidor (que
 * vuelve a calcular cada combo al cobrar y solo aplica su descuento si los renglones coinciden).
 *
 *   fijo:        necesidad = factor × combos (en una receta por medida: factor, una sola vez por trabajo)
 *   consumo:     necesidad = medida × factor
 *   rendimiento: necesidad = medida ÷ factor
 *   derivado:    necesidad = necesidad del componente base (sin redondear) × (1 + ajuste % ÷ 100)
 *   cantidad = necesidad ÷ contenido, hacia arriba al entero (o a la fracción mínima del producto)
 *
 * Con empaques alternativos: de mayor a menor contenido se toman los empaques enteros que caben, lo que queda se cubre
 * con el más chico (hacia arriba) y, si las unidades de un empaque contienen tanto como una del siguiente más grande,
 * se cambian por esa (no 2 rollos de 76 m si alcanza 1 de 150).
 */
import { formatearCantidad } from './money/formato.ts';
import { Money } from './money/money.ts';
import type { ModoComponente, RedondeoComponente, RenglonComboDTO, TipoCombo } from './schemas/combos.ts';

export interface DefinicionEmpaque {
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  contenido: string;
}

export interface DefinicionComponente {
  orden: number;
  producto_id: string;
  presentacion_id: string;
  producto_codigo: string;
  producto_nombre: string;
  /** Unidades de la presentación (1 si es la unidad). */
  unidades_presentacion: string;
  /** Fracción mínima de venta del producto, en sus unidades. */
  fraccion_minima: string;
  modo: ModoComponente;
  factor: string | null;
  contenido: string;
  base_orden: number | null;
  ajuste_pct: string | null;
  redondeo: RedondeoComponente;
  descuento_pct: string | null;
  empaques: DefinicionEmpaque[];
}

export interface DefinicionCombo {
  tipo: TipoCombo;
  descuento_pct: string;
  componentes: DefinicionComponente[];
}

// Sin «parameter properties» (readonly en el constructor): el contenedor migra con Node en modo strip-only, que carga
// packages/shared/src tal cual y no las acepta (caída del 2026-10-26 al desplegar 54b4c59).
export class ErrorCombo extends Error {
  override readonly name = 'ErrorCombo';
  readonly codigo: string;
  constructor(codigo: string, mensaje: string) {
    super(mensaje);
    this.codigo = codigo;
  }
}

const UNO = Money.parse('1');
const CIEN = Money.parse('100');
/** A 8 decimales, como se guarda: así un 3,000…001 de una división no cuenta como más de 3. */
const a8 = (m: Money) => Money.parse(m.toDB());
const num = (m: Money | string, decimales = 4) => formatearCantidad(typeof m === 'string' ? Money.parse(m) : m, decimales);

/** La medida que pide el combo: entera y positiva en el combo fijo (número de combos); positiva en la receta. */
export function validarMedidaCombo(tipo: TipoCombo, medida: string): Money {
  const m = Money.parse(medida);
  if (!m.esPositivo()) throw new ErrorCombo('medida_invalida', tipo === 'fijo' ? 'El número de combos tiene que ser mayor que cero' : 'La medida tiene que ser mayor que cero');
  if (tipo === 'fijo' && !m.eq(m.pisoMultiplo('1'))) throw new ErrorCombo('medida_invalida', 'El número de combos es entero (1, 2, 3…)');
  return m;
}

function necesidadDirecta(c: DefinicionComponente, tipo: TipoCombo, medida: Money): { necesidad: Money; cuenta: string } {
  const factor = Money.parse(c.factor ?? '0');
  if (c.modo === 'fijo') {
    if (tipo === 'fijo') return { necesidad: factor.mul(medida), cuenta: `${num(factor)} × ${num(medida)}` };
    return { necesidad: factor, cuenta: num(factor) };
  }
  if (c.modo === 'consumo') return { necesidad: medida.mul(factor), cuenta: `${num(medida)} × ${num(factor)}` };
  return { necesidad: medida.div(factor), cuenta: `${num(medida)} ÷ ${num(factor)}` };
}

/**
 * Renglones del combo para una medida (receta) o un número de combos (combo fijo), en el orden de sus componentes.
 * Lanza ErrorCombo si la medida no sirve o la definición está rota (un derivado sin base).
 */
export function calcularCombo(def: DefinicionCombo, medidaTexto: string): RenglonComboDTO[] {
  const medida = validarMedidaCombo(def.tipo, medidaTexto);
  const porOrden = new Map(def.componentes.map((c) => [c.orden, c]));
  const necesidades = new Map<number, { necesidad: Money; cuenta: string }>();
  for (const c of def.componentes) if (c.modo !== 'derivado') necesidades.set(c.orden, necesidadDirecta(c, def.tipo, medida));
  for (const c of def.componentes) {
    if (c.modo !== 'derivado') continue;
    const base = c.base_orden === null ? undefined : necesidades.get(c.base_orden);
    if (!base || porOrden.get(c.base_orden!)?.modo === 'derivado') throw new ErrorCombo('combo_roto', `${c.producto_codigo}: el componente base no existe en el combo`);
    const ajuste = Money.parse(c.ajuste_pct ?? '0');
    const signo = ajuste.esNegativo() ? '−' : '+';
    necesidades.set(c.orden, { necesidad: base.necesidad.mul(UNO.add(ajuste.div(CIEN))), cuenta: `${num(base.necesidad)} ${signo} ${num(ajuste.abs())} %` });
  }

  const renglones: RenglonComboDTO[] = [];
  for (const c of [...def.componentes].sort((a, b) => a.orden - b.orden)) {
    const { necesidad, cuenta } = necesidades.get(c.orden)!;
    const descuento = Money.parse(c.descuento_pct ?? def.descuento_pct).toDocumento();
    const comun = { orden: c.orden, necesidad: necesidad.toDB(), descuento_pct: descuento };
    if (c.empaques.length === 0) {
      const contenido = Money.parse(c.contenido);
      const sinRedondear = a8(necesidad.div(contenido));
      // La fracción mínima es de unidades: en una presentación de varias (docena, caja) no hay fracciones que contar
      if (c.redondeo === 'fraccion' && !Money.parse(c.unidades_presentacion).eq(UNO)) throw new ErrorCombo('combo_fraccion_presentacion', `${c.producto_codigo}: el redondeo a la fracción mínima solo va con la presentación de una unidad; en esta presentación usa «Hacia arriba al entero»`);
      const paso = c.redondeo === 'entero' ? UNO : Money.parse(c.fraccion_minima);
      // Una necesidad minúscula que a 8 decimales queda en 0 igual pide una unidad (la necesidad siempre es mayor que 0)
      const cantidad = sinRedondear.esPositivo() ? sinRedondear.techoMultiplo(paso) : paso;
      // El derivado lleva paréntesis si después se divide: «(100 − 50 %) ÷ 42,5»
      const conContenido = contenido.eq(UNO) ? '' : ` ÷ ${num(contenido)}`;
      const cuentaTexto = c.modo === 'derivado' && conContenido ? `(${cuenta})` : cuenta;
      renglones.push({
        ...comun,
        producto_id: c.producto_id,
        presentacion_id: c.presentacion_id,
        producto_codigo: c.producto_codigo,
        producto_nombre: c.producto_nombre,
        sin_redondear: sinRedondear.toDB(),
        cantidad: cantidad.toDB(),
        empaque: false,
        explicacion: `${cuentaTexto}${conContenido} = ${num(sinRedondear)}${cantidad.eq(sinRedondear) ? '' : ` → ${num(cantidad)}`}`,
      });
      continue;
    }
    // Empaques: el principal y los alternativos, de mayor a menor contenido (a igual contenido, el principal primero)
    const paquetes = [
      { producto_id: c.producto_id, presentacion_id: c.presentacion_id, producto_codigo: c.producto_codigo, producto_nombre: c.producto_nombre, contenido: Money.parse(c.contenido), empaque: false },
      ...c.empaques.map((e) => ({ ...e, contenido: Money.parse(e.contenido), empaque: true })),
    ].sort((a, b) => b.contenido.cmp(a.contenido));
    const cuentas = paquetes.map(() => Money.cero());
    // Una necesidad minúscula que a 8 decimales queda en 0 igual pide el empaque más chico
    let resto = a8(necesidad).esPositivo() ? a8(necesidad) : Money.parse('0.00000001');
    paquetes.forEach((p, i) => {
      if (!resto.esPositivo()) return;
      const enteros = i < paquetes.length - 1 ? resto.div(p.contenido).pisoMultiplo('1') : resto.div(p.contenido).techoMultiplo('1');
      cuentas[i] = enteros;
      resto = a8(resto.sub(enteros.mul(p.contenido)));
    });
    // Si las unidades de un empaque contienen tanto como una del siguiente más grande, se cambian por esa
    for (let i = paquetes.length - 1; i > 0; i--) {
      if (cuentas[i]!.esPositivo() && cuentas[i]!.mul(paquetes[i]!.contenido).gte(paquetes[i - 1]!.contenido)) {
        cuentas[i - 1] = cuentas[i - 1]!.add(UNO);
        cuentas[i] = Money.cero();
      }
    }
    const reparto = paquetes.flatMap((p, i) => (cuentas[i]!.esPositivo() ? [`${num(cuentas[i]!)} × ${num(p.contenido)}`] : [])).join(' + ');
    paquetes.forEach((p, i) => {
      if (!cuentas[i]!.esPositivo()) return;
      renglones.push({
        ...comun,
        producto_id: p.producto_id,
        presentacion_id: p.presentacion_id,
        producto_codigo: p.producto_codigo,
        producto_nombre: p.producto_nombre,
        sin_redondear: a8(necesidad.div(p.contenido)).toDB(),
        cantidad: cuentas[i]!.toDB(),
        empaque: p.empaque,
        explicacion: `${cuenta} = ${num(necesidad)}: ${reparto}`,
      });
    });
  }
  return renglones;
}

/**
 * Descuento de un renglón vendido en combo frente al programado (decisión del 2026-10-26, como hoy entre programado y
 * manual): rige el mayor.
 */
export function descuentoConCombo(combo: string, programado: string | null): { pct: string; origen: 'combo' | 'programado' } {
  if (programado !== null && Money.parse(programado).gt(combo)) return { pct: Money.parse(programado).toDocumento(), origen: 'programado' };
  return { pct: Money.parse(combo).toDocumento(), origen: 'combo' };
}
